/**
 * lib/employerMembers.js — Phase 0.7 Slice S7/S8 frontend client.
 *
 * Single source of truth for every lifecycle WRITE call. Every
 * function delegates to the additive FastAPI shim under
 * `/api/employer/v1/*`. Direct `supabase.rpc()` or PostgREST RPC
 * calls are FORBIDDEN — the shim is the only authority bridge.
 *
 * Identity contract: the shim derives `actor_uid` from the JWT.
 * The frontend never sends an actor identity in a body field.
 */
import { apiFetch } from '@/lib/api';

const BASE = '/api/employer/v1';

/* ─────────────────────────────────────────────────────────────
 * Single-row lifecycle
 * ───────────────────────────────────────────────────────────── */

export function inviteMember({ accountId, targetEmail, targetRole, expiresIn = '7 days', idempotencyKey }) {
  return apiFetch(`${BASE}/members/invite`, {
    method: 'POST',
    body:   { account_id: accountId, target_email: targetEmail, target_role: targetRole, expires_in: expiresIn },
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

export function revokeInvitation({ accountId, invitationId, idempotencyKey }) {
  return apiFetch(`${BASE}/invitations/${invitationId}/revoke`, {
    method: 'POST',
    body:   { account_id: accountId },
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
  });
}

export function revokeMember({ accountId, targetUserId }) {
  return apiFetch(`${BASE}/members/${targetUserId}/revoke`, {
    method: 'POST',
    body:   { account_id: accountId },
  });
}

export function modifyRole({ accountId, targetUserId, newRole }) {
  return apiFetch(`${BASE}/members/${targetUserId}/role`, {
    method: 'POST',
    body:   { account_id: accountId, new_role: newRole },
  });
}

export function suspendMember({ accountId, targetUserId, reason, expiresAt, isEmergency = false }) {
  return apiFetch(`${BASE}/members/${targetUserId}/suspend`, {
    method: 'POST',
    body:   { account_id: accountId, reason, expires_at: expiresAt, is_emergency: isEmergency },
  });
}

export function unsuspend({ accountId, suspensionId }) {
  return apiFetch(`${BASE}/suspensions/${suspensionId}/unsuspend`, {
    method: 'POST',
    body:   { account_id: accountId },
  });
}

/* ─────────────────────────────────────────────────────────────
 * Bulk lifecycle
 * ───────────────────────────────────────────────────────────── */

export function bulkRevokeInvitations({ accountId, invitationIds }) {
  return apiFetch(`${BASE}/members/bulk/invite-revoke`, {
    method: 'POST',
    body:   { account_id: accountId, invitation_ids: invitationIds },
  });
}

export function bulkRevokeMembers({ accountId, targetUserIds }) {
  return apiFetch(`${BASE}/members/bulk/revoke`, {
    method: 'POST',
    body:   { account_id: accountId, target_user_ids: targetUserIds },
  });
}

export function bulkRoleModify({ accountId, items }) {
  return apiFetch(`${BASE}/members/bulk/role-modify`, {
    method: 'POST',
    body:   { account_id: accountId, items },
  });
}

export function bulkUnsuspend({ accountId, suspensionIds }) {
  return apiFetch(`${BASE}/members/bulk/unsuspend`, {
    method: 'POST',
    body:   { account_id: accountId, suspension_ids: suspensionIds },
  });
}

/* ─────────────────────────────────────────────────────────────
 * Visible batch caps — match shim PL/pgSQL bounds.
 * ───────────────────────────────────────────────────────────── */
export const BULK_LIMITS = Object.freeze({
  inviteRevoke: 100,
  revoke:       50,
  roleModify:   50,
  unsuspend:    50,
});

export const SHIM_BASE = BASE;
